import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HelpersService } from '../../../../core/services/helpers.service';
import { FileService } from '../../../../core/api/file.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoadingService } from '../../../../core/services/loading.service';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-confirm-approval',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatProgressSpinnerModule],
  templateUrl: './confirm-approval.component.html',
  styleUrl: './confirm-approval.component.scss',
})
export class ConfirmApprovalComponent {
  form: FormGroup;
  uploadStatus: boolean;
  file: any;
  fileExists: boolean;
  fileName: any;
  uploadedFiles: any[] = [];
  approvalNote: boolean = false;
  approvalFile: boolean = false;
  
  constructor(
    public dialogRef: MatDialogRef<ConfirmApprovalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private helpers: HelpersService,
    private fileService: FileService,
    private loadingService: LoadingService,
  ) {
    this.form = this.fb.group({
      note: [null],
      attachmentId: [null],
      attachmentIds: [[]],
    });
    this.approvalNote = data.expenseCategory?.approval_note;
    this.approvalFile = data.expenseCategory?.approval_upload_file;

    if (this.approvalNote) {
      this.form.get('note')?.setValidators(Validators.required);
    }

    if (this.approvalFile) {
      this.form.get('attachmentId')?.setValidators(Validators.required);
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.form.valid) {
      this.dialogRef.close(this.form.value);
    } else {
      this.helpers.sbError('Lengkapi form terlebih dahulu!');
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  uploadFile(event: Event) {
    const target = event.target as HTMLInputElement | null;
    const fileList = target?.files;
    this.processUploadFiles(fileList ? Array.from(fileList) : [], target);
  }

  private processUploadFiles(files: File[], input?: HTMLInputElement | null) {
    this.uploadStatus = true;
    if (!files.length) {
      this.helpers.sbError('Tidak ada file yang akan diupload!');
      this.uploadStatus = false;
      if (input) input.value = '';
      return;
    }

    const uploads = files.map(
      (file) =>
        new Promise<any>((resolve, reject) => {
          try {
            this.fileService.upload(file).subscribe({
              next: (res: any) => {
                if (
                  res?.type === HttpEventType.Response &&
                  res?.body?.data
                ) {
                  resolve(res.body.data);
                }
              },
              error: (err: any) => reject(err),
            });
          } catch (err: any) {
            reject(err);
          }
        })
    );

    this.loadingService.startLoading();
    Promise.all(uploads)
      .then((results: any[]) => {
        const uploaded = results.filter(Boolean);
        if (!uploaded.length) return;
        this.uploadedFiles = [...this.uploadedFiles, ...uploaded];
        this.file = this.uploadedFiles[0];
        this.fileExists = true;
        this.fileName =
          this.uploadedFiles.length === 1
            ? this.uploadedFiles[0].fileName
            : `${this.uploadedFiles.length} files selected`;
        this.form.get('attachmentId')?.setValue(this.uploadedFiles[0].id);
        this.form
          .get('attachmentIds')
          ?.setValue(this.uploadedFiles.map((item: any) => item.id));
      })
      .catch((err: any) => {
        this.helpers.sbError(err.message);
      })
      .finally(() => {
        this.loadingService.endLoading();
        this.uploadStatus = false;
        if (input) input.value = '';
      });
  }

  handleDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    this.processUploadFiles(files ? Array.from(files) : []);
  }

  handleDragOver(event: DragEvent) {
    event.preventDefault();
  }

  downloadFile() {
    this.loadingService.startLoading();
    this.fileService.download(this.file?.id).subscribe(
      (res) => {
        this.loadingService.endLoading();
        const url = URL.createObjectURL(res);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.download = this.fileName ?? this.file?.fileName;
        anchor.click();
        URL.revokeObjectURL(url);
      },
      (error) => {
        this.loadingService.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }

  removeFile() {
    this.form.get('attachmentId')?.setValue(null);
    this.form.get('attachmentIds')?.setValue([]);
    this.uploadedFiles = [];
    this.fileExists = false;
    this.file = null;
    this.fileName = null;
  }
}
